// @flow
import React from 'react';
import {action, observable} from 'mobx';
import {observer} from 'mobx-react';
import {Button, Loader, SingleSelect} from 'sulu-admin-bundle/components';
import {Requester} from 'sulu-admin-bundle/services';
import {translate} from 'sulu-admin-bundle/utils';
import listContainerStyles from 'sulu-admin-bundle/containers/List/list.scss';
import listViewStyles from 'sulu-admin-bundle/views/List/list.scss';
import previewStyles from './preview.scss';

type RoleOption = {
    value: string,
    translationKey: string,
};

@observer
class Preview extends React.Component<{}> {
    @observable selectedRole: string = '';
    @observable loading: boolean = false;
    @observable loadingRoles: boolean = true;
    @observable error: ?string = null;
    @observable roleOptions: Array<RoleOption> = [];

    componentDidMount() {
        this.loadRoleOptions();
    }

    @action loadRoleOptions = () => {
        this.loadingRoles = true;
        this.error = null;

        Requester.get('/admin/api/preview/roles')
            .then(action((response) => {
                this.roleOptions = response.roles || [];
                this.loadingRoles = false;
            }))
            .catch(action(() => {
                this.loadingRoles = false;
                this.error = translate('sulu_admin.form_save_server_error');
            }));
    };

    @action handleRoleChange = (role: ?string | number) => {
        this.selectedRole = role ? String(role) : '';
        this.error = null;
    };

    @action handleSubmit = () => {
        this.loading = true;
        this.error = null;

        const role = '' === this.selectedRole ? null : this.selectedRole;
        const previewWindow = window.open('about:blank', '_blank');

        if (!previewWindow) {
            this.loading = false;
            this.error = translate('sulu_admin.preview.popup_blocked');
            return;
        }

        Requester.post('/admin/api/preview', {role})
            .then(action((response) => {
                const redirectUrl = response.redirectUrl;
                if (!redirectUrl) {
                    previewWindow.close();
                    throw new Error('Missing redirect URL in response.');
                }

                previewWindow.location.href = redirectUrl;
                this.loading = false;
            }))
            .catch(action((errorResponse) => {
                previewWindow.close();
                this.loading = false;

                if (errorResponse && typeof errorResponse.json === 'function') {
                    errorResponse.json().then(action((data) => {
                        this.error = data.detail || data.title || data.message
                            || translate('sulu_admin.form_save_server_error');
                    })).catch(action(() => {
                        this.error = translate('sulu_admin.form_save_server_error');
                    }));
                } else if (errorResponse && errorResponse.message) {
                    this.error = errorResponse.message;
                } else {
                    this.error = translate('sulu_admin.form_save_server_error');
                }
            }));
    };

    render() {
        return (
            <div className={listViewStyles.listContainer}>
                <div className={listContainerStyles.listContainer}>
                    <h1>{translate('sulu_admin.preview.title')}</h1>

                    <div className={previewStyles.content}>
                        <p className={previewStyles.description}>{translate('sulu_admin.preview.description')}</p>

                        <div className={previewStyles.field}>
                            <label className={previewStyles.label} htmlFor="preview-role">
                                {translate('sulu_admin.preview.role_label')}
                            </label>
                            {this.loadingRoles
                                ? <Loader />
                                : (
                                    <SingleSelect
                                        id="preview-role"
                                        onChange={this.handleRoleChange}
                                        value={this.selectedRole}
                                    >
                                        {this.roleOptions.map((option) => (
                                            <SingleSelect.Option
                                                key={option.value || 'disconnected'}
                                                value={option.value}
                                            >
                                                {translate(option.translationKey)}
                                            </SingleSelect.Option>
                                        ))}
                                    </SingleSelect>
                                )
                            }
                        </div>

                        {this.error && (
                            <p className={previewStyles.error}>{this.error}</p>
                        )}

                        <Button
                            disabled={this.loading || this.loadingRoles}
                            onClick={this.handleSubmit}
                            skin="primary"
                        >
                            {translate('sulu_admin.preview.submit')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }
}

export default Preview;
